// Session teardown — the ONE place a HR session ends.
//
// Three call sites used to each hand-roll the same three steps (revoke server
// side, wipe the in-memory token, drop the localStorage marker): the sidebar
// user menu, and `onAuthLost` in http.ts when a refresh finally fails. Any one
// of them forgetting a step leaves a resumable session behind — the refresh
// token is an httpOnly cookie, so a client-only teardown keeps the session
// alive via /auth/refresh (a shared/kiosk risk). Centralising it here makes the
// teardown unit-testable (plain CommonJS, deps injected) and means a new
// sign-out entry point cannot get it subtly wrong.
//
// `lang` is deliberately KEPT: it is a UI preference, not session state, and
// clearing it would reset the portal to Arabic on every logout.

// localStorage keys that are session state and must die with the session.
var SESSION_KEYS = ['user'];

// Keys that outlive the session (UI preferences). Documented so a future key
// lands in the right list instead of being cleared by accident.
var PRESERVED_KEYS = ['lang'];

/**
 * Remove every session key from a Storage-like object. Never throws: Safari
 * private mode and blocked site data make localStorage access itself throw.
 * @param {Storage|undefined} storage
 * @returns {string[]} the keys actually removed
 */
function clearSessionKeys(storage) {
  var removed = [];
  if (!storage) return removed;
  for (var i = 0; i < SESSION_KEYS.length; i += 1) {
    try {
      storage.removeItem(SESSION_KEYS[i]);
      removed.push(SESSION_KEYS[i]);
    } catch (e) {
      /* storage unavailable — the in-memory teardown below still runs */
    }
  }
  return removed;
}

/**
 * Full sign-out. Order matters: revoke server-side FIRST (while the bearer is
 * still in memory, so the call is authenticated), then tear down locally.
 *
 * The server call is best-effort on purpose — an offline or 500ing backend must
 * never trap the user in a session they asked to leave. Local teardown always
 * runs.
 *
 * @param {Object} deps
 * @param {function(string):Promise<any>} deps.post      POST helper (apiPost)
 * @param {{clearAuth:function():void}}   deps.tokenStore in-memory token store
 * @param {Storage}                       [deps.storage]  defaults to localStorage
 * @param {function():void}               deps.redirect   send the user to login
 * @returns {Promise<{revoked:boolean}>} revoked=false ⇒ local-only teardown
 */
function performLogout(deps) {
  var d = deps || {};
  var revoked = false;
  return Promise.resolve()
    .then(function () {
      if (typeof d.post !== 'function') return null;
      return d.post('/auth/logout').then(
        function () {
          revoked = true;
        },
        function () {
          // offline / 401 / 500 — fall through to the local teardown
        }
      );
    })
    .then(function () {
      if (d.tokenStore && typeof d.tokenStore.clearAuth === 'function') {
        d.tokenStore.clearAuth();
      }
      var storage = d.storage;
      if (storage === undefined && typeof localStorage !== 'undefined') {
        storage = localStorage;
      }
      clearSessionKeys(storage);
      if (typeof d.redirect === 'function') d.redirect();
      return { revoked: revoked };
    });
}

module.exports = {
  SESSION_KEYS: SESSION_KEYS,
  PRESERVED_KEYS: PRESERVED_KEYS,
  clearSessionKeys: clearSessionKeys,
  performLogout: performLogout,
};
